/**
 * Phase 8-G — Stats page (top-level entry, ex-Agency Stats deep-link).
 *
 * Renders the shared `StatsView` against `GET /api/v1/agency/stats`.
 * Role-gated to owner / team_lead — other roles see an inline no-access
 * message inside the legacy chrome.
 *
 * Phase 13 — promoted out of the `/agency/*` deep-link survivor. The page now
 * lives at both `/stats` (sidebar entry) AND `/agency/stats` (inbound
 * deep-links from the legacy dashboard's "Detailed stats" header). Shell
 * matches `Integrations.tsx`: `PageWrapperMain` → `subPage` →
 * `SubPageContentHolder` → `SubPageInnerContentHolder`. NO MUI primitives.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import '../../styles/pages/stats/stats.scss';

import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import { RootState } from '../../store/store';
import StatsView from '../../components/stats/StatsView';
import PerUserStatsView from '../../components/stats/PerUserStatsView';
import {
  StatsResponse,
  StatsWindow,
  getAgencyStats,
} from '../../lib/api/stats/stats-api';
import {
  PerUserStatsResponse,
  getPerUserStats,
} from '../../lib/api/audit/audit-api';
import {
  canViewStatsPage,
  useCan,
  useCapabilities,
  useEmployeeRole,
} from '../../lib/rbac';

type StatsTab = 'overview' | 'perUser';

const AgencyStats: React.FC = () => {
  const { t } = useTranslation();
  // W1 — the agency Stats page surfaces per-user / per-employee breakdowns
  // and the audit-style activity view, which the matrix restricts to
  // owner / team_lead. Drive that off the RBAC layer (`stats.perUser`)
  // rather than a local role allow-list so it stays in sync with the matrix.
  const role = useEmployeeRole();
  const accountTypeRaw = useSelector(
    (s: RootState) => s.auth.agency.employeeIdentity?.account_type
  );
  const accountType: 'agency' | 'enterprise' =
    accountTypeRaw === 'enterprise' ? 'enterprise' : 'agency';
  const capabilities = useCapabilities();
  const canView = canViewStatsPage(capabilities, role);
  // W5 — per-user breakdown is gated to its own capability (owner/team_lead).
  const canViewPerUser = useCan('stats.perUser');

  const [tab, setTab] = useState<StatsTab>('overview');
  const [window, setWindow] = useState<StatsWindow>('30d');
  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // W5 — per-user state. Loaded lazily the first time the tab is opened, then
  // re-loaded whenever the shared window changes while the tab is active.
  const [perUserData, setPerUserData] = useState<PerUserStatsResponse | null>(
    null
  );
  const [perUserLoading, setPerUserLoading] = useState(false);
  const [perUserError, setPerUserError] = useState<string | null>(null);

  const load = useCallback(async (w: StatsWindow) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAgencyStats(w);
      setData(res);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AgencyStats] load failed', err);
      setError(err instanceof Error ? err.message : 'Failed to load stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPerUser = useCallback(async (w: StatsWindow) => {
    setPerUserLoading(true);
    setPerUserError(null);
    try {
      const res = await getPerUserStats(w);
      setPerUserData(res);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AgencyStats] per-user load failed', err);
      setPerUserError(
        err instanceof Error ? err.message : 'Failed to load per-user stats.'
      );
    } finally {
      setPerUserLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView && tab === 'overview') {
      load(window);
    }
  }, [canView, window, load, tab]);

  useEffect(() => {
    if (canView && canViewPerUser && tab === 'perUser') {
      loadPerUser(window);
    }
  }, [canView, canViewPerUser, window, loadPerUser, tab]);

  /* ============================================================
     No-access state — render the legacy shell so the sidebar stays
     mounted and the user can navigate away.
     ============================================================ */
  if (!canView) {
    return (
      <PageWrapperMain>
        <div className='subPage'>
          <SubPageContentHolder>
            <SubPageInnerContentHolder
              title={t('Stats')}
              subTitle={t('StatsSubTitle')}
              backgroundColor={'var(--main-background-color)'}
            >
              <div className='statsPage'>
                <div className='statsPage__noAccess'>{t('StatsNoAccess')}</div>
              </div>
            </SubPageInnerContentHolder>
          </SubPageContentHolder>
        </div>
      </PageWrapperMain>
    );
  }

  /* ============================================================
     Main view
     ============================================================ */
  return (
    <PageWrapperMain>
      <div className='subPage'>
        <SubPageContentHolder>
          <SubPageInnerContentHolder
            title={t('Stats')}
            subTitle={t('StatsSubTitle')}
            backgroundColor={'var(--main-background-color)'}
          >
            <div className='statsPage'>
              {/* W5 — Overview / Per-user tab switcher. The per-user tab is
                  only shown when the employee holds the `stats.perUser`
                  capability (owner/team_lead). */}
              {canViewPerUser && (
                <div className='statsPage__tabs' role='tablist'>
                  <button
                    type='button'
                    role='tab'
                    aria-selected={tab === 'overview'}
                    className={`statsPage__tab ${
                      tab === 'overview' ? 'statsPage__tab--active' : ''
                    }`}
                    onClick={() => setTab('overview')}
                  >
                    {t('StatsTabOverview')}
                  </button>
                  <button
                    type='button'
                    role='tab'
                    aria-selected={tab === 'perUser'}
                    className={`statsPage__tab ${
                      tab === 'perUser' ? 'statsPage__tab--active' : ''
                    }`}
                    onClick={() => setTab('perUser')}
                  >
                    {t('StatsTabPerUser')}
                  </button>
                </div>
              )}

              {tab === 'overview' &&
                (loading && !data ? (
                  <div className='statsPage__loading'>
                    <LoadingSpinner customLoadingMsg={t('LoadingStats')} />
                  </div>
                ) : (
                  <StatsView
                    data={data}
                    window={window}
                    onWindowChange={setWindow}
                    loading={loading}
                    error={error}
                    onRetry={() => load(window)}
                    title={t('Stats')}
                    brandLinkMode='agency'
                    accountType={accountType}
                  />
                ))}

              {tab === 'perUser' && canViewPerUser && (
                <PerUserStatsView
                  data={perUserData}
                  window={window}
                  onWindowChange={setWindow}
                  loading={perUserLoading}
                  error={perUserError}
                  onRetry={() => loadPerUser(window)}
                />
              )}
            </div>
          </SubPageInnerContentHolder>
        </SubPageContentHolder>
      </div>
    </PageWrapperMain>
  );
};

export default AgencyStats;
